<template>
  <v-sheet class="d-flex align-center" style="gap: 16px;">
    <v-sheet
      v-if="showProcessSegmentation"
      class="d-flex align-center"
      style="gap: 8px;"
    >
      <v-btn
        class="seg-btn"
        :class="{ 'seg-btn--active': segmentationMode === 'general' }"
        :prepend-icon="segmentationMode === 'general' ? 'mdi-check' : undefined"
        :variant="segmentationMode === 'general' ? 'flat' : 'outlined'"
        size="small"
        @click="setSegmentation('general')"
      >
        Geral
      </v-btn>
      <v-btn
        class="seg-btn"
        :class="{'seg-btn--active': segmentationMode === 'process'}"
        :prepend-icon="segmentationMode === 'process' ? 'mdi-check' : undefined"
        :variant="segmentationMode === 'process' ? 'flat' : 'outlined'"
        size="small"
        @click="setSegmentation('process')"
      >
        Por processo
      </v-btn>

      <v-menu
        v-model="isProcessMenuOpen"
        location="bottom start"
        :close-on-content-click="true"
        v-if="segmentationMode === 'process'"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            class="seg-process-btn"
            append-icon="mdi-menu-down"
            size="small"
            variant="outlined"
          >
            {{ selectedProcessLabel }}
          </v-btn>
        </template>

        <v-list style="min-width: 260px;">
          <v-list-item v-if="isLoadingProcesses">
            <v-list-item-title>Carregando processos...</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-else-if="!processes.length"
          >
            <v-list-item-title>Nenhum processo encontrado</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-for="process in processes"
            :key="process.id"
            @click="selectProcess(process)"
          >
            <v-list-item-title>
              {{ formatProcessLabel(process) }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-sheet>

    <v-sheet class="d-flex align-center ml-auto" style="gap: 16px;">
      <v-sheet class="d-flex" style="gap: 8px;">
        <div
          v-if="showChart"
          role="button"
          tabindex="0"
          @click="setView('chart')"
          @keyup.enter="setView('chart')"
          class="pa-1 d-flex align-center justify-center"
          style="width: 40px; height: 40px"
          :class="{ 'ico-style': currentView === 'chart' }"
          aria-label="Exibir gráfico"
          :aria-pressed="currentView === 'chart'"
        >
          <svg
            v-if="currentView === 'chart'"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7 17H9V12H7V17ZM15 17H17V7H15V17ZM11 17H13V14H11V17ZM11 12H13V10H11V12ZM5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5Z"
              fill="#41546D"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M7 17H9V12H7V17ZM15 17H17V7H15V17ZM11 17H13V14H11V17ZM11 12H13V10H11V12ZM5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM5 19H19V5H5V19Z"
              fill="#505353"
            />
          </svg>
        </div>

        <div
          v-if="showTable"
          role="button"
          tabindex="0"
          @click="setView('table')"
          @keyup.enter="setView('table')"
          class="pa-1 d-flex align-center justify-center"
          style="width: 40px; height: 40px"
          :class="{ 'ico-style': currentView === 'table' }"
          aria-label="Exibir tabela"
          :aria-pressed="currentView === 'table'"
        >
          <svg
            v-if="currentView === 'table'"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M8 22H13V18H6V20C6 20.55 6.19583 21.0208 6.5875 21.4125C6.97917 21.8042 7.45 22 8 22ZM15 22H20C20.55 22 21.0208 21.8042 21.4125 21.4125C21.8042 21.0208 22 20.55 22 20V18H15V22ZM2 18V4C2 3.45 2.19583 2.97917 2.5875 2.5875C2.97917 2.19583 3.45 2 4 2H18V4H4V18H2ZM6 16H13V12H6V16ZM15 16H22V12H15V16ZM6 10H22V8C22 7.45 21.8042 6.97917 21.4125 6.5875C21.0208 6.19583 20.55 6 20 6H8C7.45 6 6.97917 6.19583 6.5875 6.5875C6.19583 6.97917 6 7.45 6 8V10Z"
              fill="#41546D"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M8 22C7.45 22 6.97917 21.8042 6.5875 21.4125C6.19583 21.0208 6 20.55 6 20V8C6 7.45 6.19583 6.97917 6.5875 6.5875C6.97917 6.19583 7.45 6 8 6H20C20.55 6 21.0208 6.19583 21.4125 6.5875C21.8042 6.97917 22 7.45 22 8V20C22 20.55 21.8042 21.0208 21.4125 21.4125C21.0208 21.8042 20.55 22 20 22H8ZM8 20H13V17H8V20ZM15 20H20V17H15V20ZM2 18V4C2 3.45 2.19583 2.97917 2.5875 2.5875C2.97917 2.19583 3.45 2 4 2H18V4H4V18H2ZM8 15H13V12H8V15ZM15 15H20V12H15V15ZM8 10H20V8H8V10Z"
              fill="#505353"
            />
          </svg>
        </div>
      </v-sheet>

      <v-sheet v-if="showSearch" width="320px">
        <v-text-field
          v-model="internalSearch"
          prepend-inner-icon="mdi-magnify"
          placeholder="Pesquisar"
          hide-details
          @keyup.enter="debouncedSearch"
          @click:prepend-inner="$emit('submit:search', internalSearch)"
        />
      </v-sheet>

      <MenuComponent
        v-model="isFilterOpen"
        :initial-filters="internalFilters"
        @apply-filters="onFiltersChanged"
        @clear-filters="clearFilters"
        :available-ids="availableIds"
        :show-processo-seletivo="enableProcessoSeletivo"
        :show-processo="enableProcesso"
        :show-modalidade="enableModalidade"
        :show-nivel-ensino="enableNivelEnsino"
        :show-curso="enableCurso"
        :show-oferta-curso="enableOfertaCurso"
        :show-etapa="enableEtapa"
        :show-faixa-conversao="enableFaixaConversao"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            v-if="showFilter"
            elevation="0"
            class="filter-style"
            :class="{ 'filter-active': hasActiveFilters }"
            @click="openFilter"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path d="M8 17V15H12V17H8ZM4 12V10H16V12H4ZM1 7V5H19V7H1Z" :fill="hasActiveFilters ? '#004F4F' : '#505353'" />
            </svg>
            <span class="text-filter-style">Filtros</span>
          </v-btn>
        </template>
      </MenuComponent>

      <v-sheet
        v-if="showRefresh"
        @click="$emit('click:refresh')"
        role="button"
        tabindex="0"
        class="pa-1 d-flex align-center justify-center"
        aria-label="Atualizar"
        style="width: 40px; height: 40px; cursor: pointer;"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12.05 19.5C9.81667 19.5 7.91667 18.725 6.35 17.175C4.78333 15.625 4 13.7333 4 11.5V11.325L2.4 12.925L1 11.525L5 7.525L9 11.525L7.6 12.925L6 11.325V11.5C6 13.1667 6.5875 14.5833 7.7625 15.75C8.9375 16.9167 10.3667 17.5 12.05 17.5C12.4833 17.5 12.9083 17.45 13.325 17.35C13.7417 17.25 14.15 17.1 14.55 16.9L16.05 18.4C15.4167 18.7667 14.7667 19.0417 14.1 19.225C13.4333 19.4083 12.75 19.5 12.05 19.5ZM19 15.475L15 11.475L16.4 10.075L18 11.675V11.5C18 9.83333 17.4125 8.41667 16.2375 7.25C15.0625 6.08333 13.6333 5.5 11.95 5.5C11.5167 5.55 11.0917 5.55 10.675 5.65C10.2583 5.75 9.85 5.9 9.45 6.1L7.95 4.6C8.58333 4.23333 9.23333 3.95833 9.9 3.775C10.5667 3.59167 11.25 3.5 11.95 3.5C14.1833 3.5 16.0833 4.275 17.65 5.825C19.2167 7.375 20 9.26667 20 11.5V11.675L21.6 10.075L23 11.475L19 15.475Z"
            fill="#505353"
          />
        </svg>
      </v-sheet>

      <v-sheet
        @click="onDownloadClick"
        role="button"
        tabindex="0"
        class="pa-1 d-flex align-center justify-center"
        aria-label="Baixar CSV"
        style="width: 40px; height: 40px; cursor: pointer;"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18V15H6V18H18V15H20V18C20 18.55 19.8042 19.0208 19.4125 19.4125C19.0208 19.8042 18.55 20 18 20H6Z" fill="#505353"/>
        </svg>
      </v-sheet>

    </v-sheet>
  </v-sheet>
</template>

<script>
import MenuComponent from './filters/MenuComponent.vue'
import { Get } from '@/ai_studio/http/http'

export default {
  name: 'FilterComponent',
  components: { MenuComponent },
  props: {
    availableIds: { type: Object, default: () => ({}) },
    showChart: { type: Boolean, default: true },
    showTable: { type: Boolean, default: true },
    showSearch: { type: Boolean, default: true },
    showFilter: { type: Boolean, default: true },
    showRefresh: { type: Boolean, default: true },
    showProcessSegmentation: { type: Boolean, default: false },
    showDownload: { type: Boolean, default: false },
    downloadDisabled: { type: Boolean, default: false },
    search: { type: String, default: '' },
    filters: { type: Object, default: () => ({}) },
    view: {
      type: String,
      default: 'chart',
      validator: v => ['chart', 'table'].includes(v)
    },
    enableProcessoSeletivo: { type: Boolean, default: true },
    enableProcesso: { type: Boolean, default: true },
    enableModalidade: { type: Boolean, default: true },
    enableNivelEnsino: { type: Boolean, default: true },
    enableCurso: { type: Boolean, default: true },
    enableOfertaCurso: { type: Boolean, default: true },
    enableEtapa: { type: Boolean, default: true },
    enableFaixaConversao: { type: Boolean, default: true }
  },
  emits: [
    'click:chart',
    'click:table',
    'update:search',
    'submit:search',
    'click:filter',
    'click:refresh',
    'update:view',
    'update:filters',
    'update:segmentation',
    'update:selectedProcess',
    'click:download',
  ],
  data () {
    return {
      internalSearch: this.search,
      currentView: this.view || 'chart',
      isFilterOpen: false,
      internalFilters: { ...this.filters },
      searchTimeout: null,

      segmentationMode: 'general',
      isProcessMenuOpen: false,
      processes: [],
      isLoadingProcesses: false,
      selectedProcess: null
    }
  },
  computed: {
    selectedProcessLabel () {
      if (this.selectedProcess) {
        return this.formatProcessLabel(this.selectedProcess)
      }
      return 'Processo: selecionar'
    },
    hasActiveFilters () {
      return Object.keys(this.internalFilters).length > 0
    }
  },
  watch: {
    search (val) {
      if (val !== this.internalSearch) this.internalSearch = val
    },
    view (val) {
      if (val && val !== this.currentView) this.currentView = val
    },
    filters: {
      deep: true,
      handler (val) {
        this.internalFilters = { ...val }
      }
    },
    internalSearch (newVal) {
      this.$emit('update:search', newVal)
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.$emit('submit:search', newVal)
      }, 1000)
    },
    showProcessSegmentation (val) {
      if (val) {
        this.ensureProcessesLoaded()
      }
    }
  },
  mounted () {
    if (this.showProcessSegmentation) {
      this.ensureProcessesLoaded()
    }
  },
  beforeUnmount () {
    clearTimeout(this.searchTimeout)
  },
  methods: {
    setView (next) {
      if (next === this.currentView) return
      this.currentView = next
      if (next === 'chart') this.$emit('click:chart')
      if (next === 'table') this.$emit('click:table')
      this.$emit('update:view', next)
    },
    openFilter () {
      this.$emit('click:filter')
    },
    onFiltersChanged (newFilters) {
      this.internalFilters = newFilters
      this.$emit('update:filters', newFilters)
    },
    debouncedSearch () {
      clearTimeout(this.searchTimeout)
      this.$emit('submit:search', this.internalSearch)
    },
    clearFilters () {
      this.internalFilters = []
      this.$emit('update:filters', {})
    },

    setSegmentation (mode) {
      if (this.segmentationMode === mode) return
      this.segmentationMode = mode
      this.$emit('update:segmentation', mode)
      
      if (mode === 'process') {
        this.ensureProcessesLoaded()
      } else {
        this.selectedProcess = null
      }
    },

    async ensureProcessesLoaded () {
      if (this.processes.length || this.isLoadingProcesses) return

      try {
        this.isLoadingProcesses = true

        const response = await Get('listprocess?contexto=1')
        const allProcesses = response || []

        const allowedIds = this.availableIds?.processo

        if (!Array.isArray(allowedIds) || !allowedIds.length) {
          this.processes = []
          return
        }

        const allowedSet = new Set(allowedIds.map(id => String(id)))

        this.processes = allProcesses.filter(proc =>
          allowedSet.has(String(proc.id))
        )

      } catch (err) {
        console.error('Erro ao carregar processos', err)
      } finally {
        this.isLoadingProcesses = false
      }
    },

    formatProcessLabel (process) {
      const nome = process.nome || process.name || 'Processo'
      const etapas = process.etapas.length
      return `${nome} - ${etapas} etapas`
    },

    selectProcess (process) {
      this.selectedProcess = process
      this.$emit('update:selectedProcess', process)
      this.isProcessMenuOpen = false
    },

    onDownloadClick () {
      if (this.downloadDisabled) return
      this.$emit('click:download')
    },
  }
}
</script>

<style scoped>
.ico-style {
  border-radius: 100px;
  background: var(--surfaces-surface-tint-8, rgba(0, 106, 106, 0.08));
}
.filter-style {
  width: 101px;
  height: 40px;
  border-radius: 100px;
  border: 1px solid var(--Border-Neutral-Secondary, #C5C7C6);
}
.filter-style.filter-active {
  border-radius: 14px;
  border-color: #C5C7C6;
}

.filter-style.filter-active span{
  color: #004F4F !important;
}

.text-filter-style {
  margin-left: 8px;
  margin-right: 8px;
  color: var(--Text-Neutral-Subtle, #505353);
  font-family: var(--Typescale-Label-Large-Font-family, Roboto);
  font-size: var(--Typescale-Label-Large-Font-size, 14px);
  font-style: normal;
  font-weight: 500;
  line-height: var(--Typescale-Label-Large-Line-height, 20px);
  letter-spacing: var(--Typescale-Label-Large-Tracking, 0.1px);
}

 .seg-btn {
  min-width: unset !important;
  padding-inline: 16px !important;
  height: 33px !important;
  border-radius: 10px !important;
  text-transform: none !important;
}

.seg-btn--active {
  padding-inline: 16px !important;
  background: var(--surfaces-surface-tint-8, rgba(0, 106, 106, 0.08));
  color: #41546D;
}
.seg-btn.v-btn--variant-outlined {
  border-color: #C5C7C6 !important;
}

.seg-process-btn {
  text-transform: none;
  border-radius: 8px;
  height: 33px !important;
  padding-inline: 16px !important;
  border: 1px solid var(--Border-Neutral-Secondary, #C5C7C6);
  background: var(--Background-Neutral-Secondary, #F8FAF9);
}

div[role="button"].pa-1 {
  border-radius: 100px !important;
  transition: all 0.2s ease;
  outline: none;
  background-color: transparent;
}

div[role="button"].pa-1:not(.ico-style):hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

div[role="button"].pa-1:not(.ico-style):hover svg path {
  fill: #41546D !important;
}

.ico-style {
  background: var(--surfaces-surface-tint-8, rgba(0, 106, 106, 0.08)) !important;
}

.ico-style svg path {
  fill: #41546D !important;
}

div[role="button"].pa-1:not(.ico-style) svg path {
  fill: #505353;
}
</style>
